<script generic="P" lang="ts" setup>
import { GpModal } from '@gportal/uikit';
import { ref } from 'vue';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { type IconDefinition } from '@fortawesome/fontawesome-svg-core';
import ModalFooterBtn from './ModalFooterBtn.vue';

const props = withDefaults(
  defineProps<{
    show: boolean;
    isValid?: boolean;
    headerText: string;
    headerIcon: string[] | IconDefinition;
    confirmText?: string;
    /**
     * Function that will be called when the confirm button is clicked.
     *
     * **NOTE**: errors will be caught and displayed as a notification.
     */
    asyncFunction: () => Promise<P> | void;
  }>(),
  {
    confirmText: 'Save',
    isValid: true,
  },
);

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'done', result: P): void;
}>();

const isLoading = ref(false);
const { addNotification } = useNotifications();
const onConfirm = async () => {
  isLoading.value = true;

  try {
    const response = await props.asyncFunction();
    if (response != null) {
      emit('done', response as P);
    }
  } catch (error) {
    addNotification({
      message: (error as Error).message,
      type: 'danger',
    });
  } finally {
    isLoading.value = false;
  }
};

const onCancel = () => {
  if (props.show && !isLoading.value) emit('close');
};
</script>

<template>
  <GpModal
    :close-on-backdrop="false"
    :close-on-escape="!isLoading"
    :confirm-on-enter="!isLoading"
    :header-icon="headerIcon"
    :header-text="headerText"
    :is-opened="show"
    @close="onCancel"
    @confirm="onConfirm"
  >
    <template #content>
      <slot name="content"></slot>
    </template>

    <template #footer>
      <div class="tw-flex tw-flex-wrap tw-justify-center tw-gap-4">
        <ModalFooterBtn
          v-playwright="'async-modal-cancel-btn'"
          :disabled="isLoading"
          btn-type="cancel"
          label="Cancel"
          size="sm"
          @click="onCancel"
        />
        <ModalFooterBtn
          v-playwright="'async-modal-confirm-button'"
          :disabled="isLoading || !isValid"
          :label="confirmText"
          size="sm"
          @click="onConfirm"
        />
      </div>
    </template>
  </GpModal>
</template>

<style scoped></style>
